
import { FC, useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { /* Form, SubmitButton */ } from "capitalrx-components-2.0/lib/forms";
import { Button, Buttons, Tag, Message, Messages, Tooltip } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, Grid, useResponsive, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";

import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";

import { NDCDropdown } from "./NDCDropdown";

/* TODO: uncomment with formulary data ticket followup */
// import { formularyGetByMemberAPI, formularyListByIdAPI } from "@/api";

export interface AccumulationsData {
  data: {
    family_accumulated: string;
    family_maximum: string;
    individual_accumulated: string;
    individual_maximum: string;
  };
}

export interface AccumulationsBarChartProps {
  accumulations: Record<string, AccumulationsData["data"]>;
}

interface DrugCoverageFormData {
  ndc: string;
  ndc_label?: string; // Store the full label with drug name
}

interface DrugCoverageResult {
  drug_name: string;
  network: string;
  formulary_tier: string;
  edits: string[];
  days_supply: string;
  quantity_limit?: string | number | null;
}

export const DrugCoverage: FC<{ member?: any }> = ({ member }) => {
  // Use parent form context (BenefitSearchForm provides Form) to avoid nested <form> elements
  const form = useFormContext<DrugCoverageFormData>();
  const ndcValue = useWatch({ control: form?.control, name: "ndc" });
  const [results, setResults] = useState<DrugCoverageResult[]>([]);
  /* TODO: uncomment with formulary data ticket followup */
  // const [rawFormularyRecords, setRawFormularyRecords] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // const [formularyId, setFormularyId] = useState<string | null>(null);
  const [searchedNdc, setSearchedNdc] = useState<string | null>(null);

  const displayMode = useResponsiveDisplayMode();
  const isMobile = displayMode === "mobile";

  const { four, two } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);

  const handleSearch = useCallback(() => {
    console.log("DrugCoverage handleSearch called, ndcValue:", ndcValue);

    if (!ndcValue) {
      console.log("No NDC value selected");
      setErrorMessage(null);
      return;
    }

    try {
      // Randomize fake data
      const tiers = ["1", "2", "3", "X"];
      const networks = ["In-Network", "Out-of-Network", "Specialty"];
      const editOptions = [["PA"], ["ST"], ["QL"], ["PA", "ST"], ["OTC"], []];
      const dosageForms = ["tablet", "capsule", "solution", "injection", "cream"];
      const strengths = ["5mg", "10mg", "20mg", "50mg", "100mg", "150mg", "250mg"];

      const randomTier = tiers[Math.floor(Math.random() * tiers.length)];
      const randomNetwork = networks[Math.floor(Math.random() * networks.length)];
      const randomEdits = editOptions[Math.floor(Math.random() * editOptions.length)];
      const randomDosageForm = dosageForms[Math.floor(Math.random() * dosageForms.length)];
      const randomStrength = strengths[Math.floor(Math.random() * strengths.length)];

      // Build fake drug name with random dosage info
      const drugName = `Drug ${ndcValue.slice(-4)} ${randomStrength} ${randomDosageForm}`;

      setSearchedNdc(ndcValue);
      setErrorMessage(null);

      console.log("Creating mock result with drugName:", drugName);

      const mockResult: DrugCoverageResult = {
        drug_name: drugName,
        network: randomNetwork,
        formulary_tier: randomTier,
        edits: randomEdits,
        days_supply: "30",
        quantity_limit: "30/30",
      };

      console.log("Adding result:", mockResult);
      setResults((prevResults) => {
        const newResults = [...prevResults, mockResult];
        console.log("Updated results:", newResults);
        return newResults;
      });
    } catch (err) {
      console.error("Failed to create drug result:", err);
      setErrorMessage("Failed to create drug coverage result");
    }
  }, [ndcValue]);

  const getEditBadge = (edit: string) => {
    switch (edit) {
      case "OTC":
        return <Tag type="info-high-contrast">OTC</Tag>;
      case "PA":
        return <Tag type="success-high-contrast">PA</Tag>;
      case "ST":
        return <Tag type="error-high-contrast">ST</Tag>;
      case "QL":
        return <Tag type="error-high-contrast">QL</Tag>;
      default:
        return null;
    }
  };

  return (
    <Stack spacing="medium-large">
      <Stack spacing="small">
        <NDCDropdown name="ndc" label="Drug" />
        <Body size="small" style={{ color: "#666" }}>
          Search with a minimum of 4 characters for drug name, NDC/GPI, or exact match for HCPCS code
        </Body>
        <Shelf>
          <Button
            appearance="primary"
            onClick={(e: any) => {
              // prevent parent form submission/navigation
              e?.preventDefault?.();
              handleSearch();
            }}
          >
            Search
          </Button>
          <Shelf.Spacer />
        </Shelf>
      </Stack>

      {errorMessage && (
        <Message appearance={Messages.Error} visible>
          {errorMessage}
        </Message>
      )}

      {searchedNdc && results.length === 0 && !errorMessage && (
        <Message appearance={Messages.Warning} visible>
          This drug is not part of the member's formulary.
        </Message>
      )}

      {results.length > 0 && (
        <Stack spacing="small">
          {isMobile ? (
            // Mobile view
            results.map((result, index) => (
              <Stack key={index} spacing="small" style={{ paddingTop: "16px", paddingBottom: "16px", borderBottom: "1px solid #E0E0E0" }}>
                <Shelf spacing="small">
                  <Semibold style={{ fontSize: "16px" }}>{result.drug_name}</Semibold>
                  <Shelf.Spacer />
                  <Shelf spacing="mini">
                    <Tooltip alignment="top" content="View formulary for more drug info">
                      {(ref) => (
                        <Button ref={ref} appearance={Buttons.Functional.Tertiary.Small} icon="info-circle">
                          <span style={{ display: "none" }}>Info</span>
                        </Button>
                      )}
                    </Tooltip>
                    <Tooltip alignment="top" content="Find best price">
                      {(ref) => (
                        <Button ref={ref} appearance={Buttons.Functional.Tertiary.Small} icon="dollar-circle">
                          <span style={{ display: "none" }}>Cost</span>
                        </Button>
                      )}
                    </Tooltip>
                  </Shelf>
                </Shelf>

                <Shelf spacing="small">
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>Network</Body>
                  <Shelf.Spacer />
                  <Body>{result.network}</Body>
                </Shelf>

                <Shelf spacing="small">
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>Tier</Body>
                  <Shelf.Spacer />
                  <Body>Tier {result.formulary_tier}</Body>
                </Shelf>

                <Shelf spacing="small">
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>Edits</Body>
                  <Shelf.Spacer />
                  <Shelf spacing="mini">
                    {result.edits.length > 0 ? (
                      result.edits.map((edit, idx) => (
                        <span key={idx}>{getEditBadge(edit)}</span>
                      ))
                    ) : <Body>--</Body>}
                  </Shelf>
                </Shelf>

                <Shelf spacing="small">
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>Days Supply</Body>
                  <Shelf.Spacer />
                  <Body>{result.quantity_limit || "--"}</Body>
                </Shelf>
              </Stack>
            ))
          ) : (
            // Desktop view
            <>
              {/* Header row */}
              <Grid style={{ borderBottom: "1px solid #E0E0E0", paddingBottom: "8px" }}>
                <Grid.Span columns={four}>
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>DRUG & NETWORK</Body>
                </Grid.Span>
                <Grid.Span columns={two}>
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>FORMULARY TIER</Body>
                </Grid.Span>
                <Grid.Span columns={two}>
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>EDITS</Body>
                </Grid.Span>
                <Grid.Span columns={two}>
                  <Body size="small" style={{ fontWeight: 600, color: "#60646D" }}>DAYS SUPPLY</Body>
                </Grid.Span>
                <Grid.Span columns={two}>
                  <div></div>
                </Grid.Span>
              </Grid>

              {results.map((result, index) => (
                <Grid key={index} style={{ paddingTop: "12px", paddingBottom: "12px" }}>
                  <Grid.Span columns={four}>
                    <Stack spacing="none">
                      <Semibold style={{ fontSize: "16px" }}>{result.drug_name}</Semibold>
                      <Body size="small" style={{ color: "#60646D" }}>{result.network}</Body>
                    </Stack>
                  </Grid.Span>
                  <Grid.Span columns={two}>
                    <Body>Tier {result.formulary_tier}</Body>
                  </Grid.Span>
                  <Grid.Span columns={two}>
                    <Shelf spacing="mini" style={{ justifyContent: "flex-start" }}>
                      {result.edits.length > 0 ? (
                        result.edits.map((edit, idx) => (
                          <span key={idx}>{getEditBadge(edit)}</span>
                        ))
                      ) : <span>--</span>}
                    </Shelf>
                  </Grid.Span>
                  <Grid.Span columns={two}>
                    <Body>{result.quantity_limit || "--"}</Body>
                  </Grid.Span>
                  <Grid.Span columns={two}>
                    <Shelf spacing="mini">
                      <Tooltip alignment="top" content="View formulary for more drug info">
                        {(ref) => (
                          <Button ref={ref} appearance={Buttons.Functional.Tertiary.Small} icon="info-circle">
                            <span style={{ display: "none" }}>Info</span>
                          </Button>
                        )}
                      </Tooltip>
                      <Tooltip alignment="top" content="Find best price">
                        {(ref) => (
                          <Button ref={ref} appearance={Buttons.Functional.Tertiary.Small} icon="dollar-circle">
                            <span style={{ display: "none" }}>Cost</span>
                          </Button>
                        )}
                      </Tooltip>
                    </Shelf>
                  </Grid.Span>
                </Grid>
              ))}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};
